/**
 * Skeleton.tsx — 加载态骨架库（移植自原型 loading.jsx）
 *
 * 统一「数据从后端返回前」的占位反馈：骨架屏 (skeleton) 为主，占位件与真实
 * 布局同形同尺，数据到达后无位移地替换；路由切换叠加顶部细进度条 (TopBar)；
 * 局部 / 按钮加载用 Spinner。所有占位色走 token，自动适配明暗 / 密度。
 *
 * 关键适配：原型用假定时器 (window.Loading.useDataLoad) 模拟加载，本文件
 * **不移植** 该 hook —— 实现层用 TanStack Query 真实 isLoading 来门控骨架，
 * 由页面 `const { data, isLoading } = useXxx()` 决定渲染骨架还是真实组件。
 *
 * 用法：
 *   if (isLoading) return <TableSkeleton rows={10} cols={6} />;
 *   return <RealTable />;
 * 或路由级：<PageSkeleton page="books" />
 */

import type { CSSProperties, ReactNode } from 'react';
import { Icons } from '~/lib/icons';
import { Table } from '~/ui/Table';

// ---------------------------------------------------------
// Sk — 骨架块基元。w/h 数字按 px，字符串原样；r 圆角。
// ---------------------------------------------------------
interface SkProps {
  /** 宽度：数字按 px，字符串原样（如 "70%"） */
  w?: number | string;
  /** 高度：数字按 px，字符串原样 */
  h?: number | string;
  /** 圆角（px）；circle 优先 */
  r?: number;
  /** 圆形（borderRadius 999） */
  circle?: boolean;
  style?: CSSProperties;
}

function Sk({ w = '100%', h = 12, r, circle, style }: SkProps) {
  return (
    <span
      className={`sk${circle ? ' sk-circle' : ''}`}
      style={{
        width: w,
        height: h,
        borderRadius: circle ? 999 : r != null ? r : undefined,
        ...style,
      }}
    />
  );
}

// SkText — 多行文本骨架，最后一行更短。
interface SkTextProps {
  lines?: number;
  h?: number | string;
  gap?: number;
  lastWidth?: number | string;
}

function SkText({ lines = 3, h = 11, gap = 8, lastWidth = '55%' }: SkTextProps) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Sk key={i} h={h} w={i === lines - 1 ? lastWidth : '100%'} />
      ))}
    </span>
  );
}

// Spinner — 局部加载圆环。
interface SpinnerProps {
  size?: number;
  stroke?: number;
  style?: CSSProperties;
}

function Spinner({ size = 16, stroke = 2, style }: SpinnerProps) {
  return (
    <span
      className="spinner"
      style={{ width: size, height: size, borderWidth: stroke, ...style }}
    />
  );
}

// TopBar — 顶部不确定进度条。
interface TopBarProps {
  style?: CSSProperties;
}

function TopBar({ style }: TopBarProps) {
  // 不确定进度条：用 role=status + aria-busy 表达「加载中」live region，
  // 而非 progressbar（后者需 aria-valuenow，不适合无确定进度的场景）
  return (
    <div className="loadbar" style={style} role="status" aria-busy="true" aria-label="加载中" />
  );
}

// CenterLoader — 居中 spinner + 文案，用于无明确形状的区域。
interface CenterLoaderProps {
  label?: ReactNode;
  minHeight?: number | string;
}

function CenterLoader({ label = '加载中…', minHeight = 200 }: CenterLoaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        minHeight,
        color: 'var(--fg-muted)',
      }}
    >
      <Spinner size={22} stroke={2.5} />
      <span style={{ fontSize: 12 }}>{label}</span>
    </div>
  );
}

// =========================================================
// 布局级骨架 —— 与真实组件同形
// =========================================================

// 页头骨架：标题块 + 右侧动作位
interface HeaderSkeletonProps {
  actions?: number;
}

function HeaderSkeleton({ actions = 2 }: HeaderSkeletonProps) {
  return (
    <div className="page-header" style={{ alignItems: 'flex-end' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Sk w={200} h={24} r={6} />
        <Sk w={320} h={12} />
      </div>
      <div className="hstack" style={{ gap: 8 }}>
        {Array.from({ length: actions }).map((_, i) => (
          <Sk key={i} w={i === actions - 1 ? 104 : 64} h={28} r={6} />
        ))}
      </div>
    </div>
  );
}

// 过滤行骨架
function FilterSkeleton() {
  return (
    <div className="hstack" style={{ gap: 8, padding: '0 0 4px' }}>
      <Sk w={240} h={30} r={6} />
      <Sk w={120} h={30} r={6} />
      <Sk w={120} h={30} r={6} />
      <Sk w={100} h={30} r={6} />
      <div style={{ flex: 1 }} />
      <Sk w={90} h={12} />
    </div>
  );
}

// 表格骨架 —— 复刻 .tbl 头 + 行
interface TableSkeletonProps {
  rows?: number;
  cols?: number;
  checkbox?: boolean;
}

function TableSkeleton({ rows = 10, cols = 6, checkbox = true }: TableSkeletonProps) {
  const widths = ['46%', '60%', '70%', '50%', '64%', '40%', '56%', '44%'];
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <Table className="tbl">
        <thead>
          <tr>
            {checkbox && (
              <th style={{ width: 32 }}>
                <Sk w={13} h={13} r={3} />
              </th>
            )}
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i}>
                <Sk w={i === 0 ? 70 : 48} h={9} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {checkbox && (
                <td>
                  <Sk w={13} h={13} r={3} />
                </td>
              )}
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}>
                  {c === 0 ? (
                    <div className="hstack" style={{ gap: 10 }}>
                      <Sk w={30} h={30} r={6} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <Sk w={120} h={11} />
                        <Sk w={72} h={9} />
                      </div>
                    </div>
                  ) : (
                    <Sk w={widths[(c + r) % widths.length]} h={11} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// KPI 条骨架 —— 复刻 StatCard
interface KpiSkeletonProps {
  count?: number;
  framed?: boolean;
}

function KpiSkeleton({ count = 5, framed }: KpiSkeletonProps) {
  if (framed) {
    return (
      <div
        className="card"
        style={{ display: 'grid', gridTemplateColumns: `repeat(${count}, 1fr)` }}
      >
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            style={{
              padding: 14,
              borderRight: i < count - 1 ? '1px solid var(--border)' : 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <Sk w={60} h={9} />
            <Sk w={96} h={22} r={5} />
            <Sk w={54} h={9} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: `repeat(${count}, minmax(0,1fr))`, gap: 12 }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="card"
          style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <Sk w={64} h={9} />
          <Sk w={'70%'} h={22} r={5} />
          <div className="hstack" style={{ justifyContent: 'space-between' }}>
            <Sk w={48} h={9} />
            <Sk w={84} h={26} r={4} />
          </div>
        </div>
      ))}
    </div>
  );
}

// 图表卡骨架 —— 标题 + faux 柱形 / 区域
interface ChartSkeletonProps {
  height?: number;
  title?: number;
}

function ChartSkeleton({ height = 200, title = 130 }: ChartSkeletonProps) {
  const bars = 24;
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="hstack" style={{ justifyContent: 'space-between', marginBottom: 14 }}>
        <Sk w={title} h={12} />
        <Sk w={88} h={10} />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height }}>
        {Array.from({ length: bars }).map((_, i) => {
          const hh = 30 + Math.abs(Math.sin(i * 0.7)) * 64;
          return <Sk key={i} w={`${100 / bars}%`} h={`${hh}%`} r={3} />;
        })}
      </div>
    </div>
  );
}

// 卡片网格骨架 —— 复刻 AppCard
interface CardGridSkeletonProps {
  count?: number;
  minWidth?: number;
}

function CardGridSkeleton({ count = 8, minWidth = 280 }: CardGridSkeletonProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`,
        gap: 12,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="card"
          style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
            <Sk w={38} h={38} r={8} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Sk w={'70%'} h={13} />
              <Sk w={'50%'} h={9} />
            </div>
            <Sk w={20} h={20} r={5} />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 6,
              paddingTop: 10,
              borderTop: '1px dashed var(--border)',
            }}
          >
            {[0, 1, 2].map((j) => (
              <div key={j} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <Sk w={'60%'} h={13} />
                <Sk w={'40%'} h={8} />
              </div>
            ))}
          </div>
          <Sk w={'100%'} h={26} r={4} />
          <div
            className="hstack"
            style={{
              gap: 4,
              flexWrap: 'wrap',
              paddingTop: 6,
              borderTop: '1px dashed var(--border)',
            }}
          >
            {[44, 52, 40, 60].map((w, j) => (
              <Sk key={j} w={w} h={18} r={999} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Section 卡骨架 —— 标题头 + 若干表单行
interface SectionSkeletonProps {
  rows?: number;
  label?: string;
}

// label 仅作语义占位标识，与原型一致（不渲染文字，骨架本身无文案）
function SectionSkeleton({ rows = 4 }: SectionSkeletonProps) {
  return (
    <div className="card" style={{ padding: '4px 18px 14px' }}>
      <div
        style={{
          padding: '12px 0',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
        }}
      >
        <Sk w={140} h={13} />
        <Sk w={260} h={10} />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="hstack"
          style={{
            gap: 16,
            padding: '14px 0',
            borderBottom: '1px solid var(--border)',
            alignItems: 'flex-start',
          }}
        >
          <Sk w={150} h={12} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Sk w={`${50 + (i % 3) * 14}%`} h={30} r={6} />
          </div>
        </div>
      ))}
    </div>
  );
}

// 详情页骨架 —— 返回 + 大页头 + KPI 条 + tabs + section
// onBack 在场时顶部渲染**可用**的返回按钮（与详情页同款 ghost「← 返回」），
// 让详情数据慢/挂起时操作员仍有页内退出路径；省略时退回非交互的占位条（向后兼容）。
function DetailSkeleton({ onBack }: { onBack?: () => void }) {
  return (
    <div className="page page-enter">
      {onBack ? (
        <div className="hstack" style={{ gap: 8, fontSize: 12, color: 'var(--fg-muted)' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
            <Icons.IconChevLeft size={12} />
            返回
          </button>
        </div>
      ) : (
        <Sk w={120} h={22} r={6} />
      )}
      <div className="page-header" style={{ alignItems: 'center' }}>
        <div className="hstack" style={{ gap: 14 }}>
          <Sk w={52} h={52} r={10} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Sk w={180} h={22} r={6} />
            <Sk w={300} h={11} />
          </div>
        </div>
      </div>
      <KpiSkeleton count={5} framed />
      <div
        style={{
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          gap: 18,
          marginTop: 6,
          padding: '0 4px 9px',
        }}
      >
        {[60, 80, 90, 70, 64, 90].map((w, i) => (
          <Sk key={i} w={w} h={12} />
        ))}
      </div>
      <SectionSkeleton rows={4} />
    </div>
  );
}

// =========================================================
// PageSkeleton — 路由级：按页面类型选骨架原型
// =========================================================

/** PageSkeleton 接受的页面类型 key（与原型同集） */
export type PageSkeletonKey =
  | 'dashboard'
  | 'apps'
  | 'books'
  | 'users'
  | 'finance'
  | 'feedback'
  | 'push'
  | 'ops'
  | 'settings'
  | 'profile'
  | 'paywall'
  | 'system-config'
  // 其它非表格 / 非看板页统一走 KPI + sections 兜底
  | (string & {});

// 表格型页面集合（filter + table 骨架）
const TABLE_PAGES: Record<string, 1> = { books: 1, users: 1, finance: 1, feedback: 1, push: 1 };

interface PageSkeletonProps {
  page?: PageSkeletonKey;
}

function PageSkeleton({ page = 'dashboard' }: PageSkeletonProps) {
  let body: ReactNode;
  if (page === 'dashboard') {
    body = (
      <>
        <KpiSkeleton count={5} />
        <div
          style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 12 }}
        >
          <ChartSkeleton height={220} />
          <ChartSkeleton height={220} title={100} />
        </div>
        <TableSkeleton rows={6} cols={5} />
      </>
    );
  } else if (page === 'apps') {
    body = (
      <>
        <FilterSkeleton />
        <CardGridSkeleton count={9} />
      </>
    );
  } else if (TABLE_PAGES[page]) {
    body = (
      <>
        <FilterSkeleton />
        <TableSkeleton rows={11} cols={6} />
      </>
    );
  } else {
    // paywall / ops / settings / profile / system-config 等
    body = (
      <>
        <KpiSkeleton count={4} />
        <SectionSkeleton rows={4} />
        <SectionSkeleton rows={3} />
      </>
    );
  }
  return (
    <div className="page page-enter">
      <HeaderSkeleton actions={page === 'dashboard' ? 1 : 2} />
      {body}
    </div>
  );
}

export {
  Sk,
  SkText,
  Spinner,
  TopBar,
  CenterLoader,
  HeaderSkeleton,
  FilterSkeleton,
  TableSkeleton,
  KpiSkeleton,
  ChartSkeleton,
  CardGridSkeleton,
  SectionSkeleton,
  DetailSkeleton,
  PageSkeleton,
};
